import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { integrationService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, PlugZap, Save, Trash2, Upload, FileSpreadsheet, Webhook, Copy,
  Workflow, Play, Power, Shuffle, GitBranch, Plus, RefreshCw,
} from "lucide-react";
import {
  ConnectorIcon, TenantPicker, useTenantScope, PageHeader, Badge, STATUS_STYLE, RUN_STYLE, fmtDate,
} from "./shared";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function CredentialForm({ integration, authSchema, params, onSaved }) {
  const [form, setForm] = useState(() => {
    const init = {};
    (authSchema || []).forEach((f) => { init[f.key] = integration.credentials?.[f.key] ?? f.default ?? ""; });
    return init;
  });
  const [saving, setSaving] = useState(false);
  if (!authSchema || authSchema.length === 0) return null;

  const save = async () => {
    setSaving(true);
    try {
      // don't resend masked secret values
      const creds = {};
      authSchema.forEach((f) => {
        const v = form[f.key];
        if (f.secret && typeof v === "string" && v.includes("•")) return;
        creds[f.key] = v;
      });
      await integrationService.connect(integration.id, { credentials: creds }, params);
      toast.success("Credentials saved securely");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid="credential-form">
      <h2 className="text-sm font-bold text-foreground flex items-center gap-2"><PlugZap className="h-4 w-4 text-primary" /> Credentials</h2>
      {authSchema.map((f) => (
        <div key={f.key}>
          <label className="text-xs text-muted-foreground">{f.label}{f.secret && <span className="text-amber-600"> · stored securely</span>}</label>
          {f.type === "select" ? (
            <select value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground mt-1" data-testid={`cred-${f.key}`}>
              {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input type={f.secret ? "password" : "text"} value={form[f.key]} placeholder={f.placeholder || ""}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground mt-1" data-testid={`cred-${f.key}`} />
          )}
        </div>
      ))}
      <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-3 py-2 text-xs font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid="cred-save">
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save credentials
      </button>
    </div>
  );
}

function FileUploadCard({ integration, params, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File too large (max 10MB)");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await integrationService.upload(integration.id, fd, params);
      setInfo(r);
      toast.success(`Staged ${r.rowCount} record(s)`);
      onUploaded();
    } catch (err) { toast.error(apiErrorMessage(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid="file-upload-card">
      <h2 className="text-sm font-bold text-foreground flex items-center gap-2"><FileSpreadsheet className="h-4 w-4 text-primary" /> Upload data (CSV / JSON)</h2>
      <p className="text-xs text-muted-foreground">{integration.sampleRowCount ? `${integration.sampleRowCount} row(s) currently staged.` : "No file staged yet."}</p>
      <label className="inline-flex items-center gap-1.5 border border-border rounded-lg px-3 py-2 text-xs font-semibold text-foreground hover:bg-muted cursor-pointer w-fit" data-testid="file-upload-label">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Choose file
        <input type="file" accept=".csv,.json" className="hidden" onChange={upload} data-testid="file-upload-input" />
      </label>
      {info && <p className="text-[11px] text-muted-foreground">Columns: {(info.columns || []).join(", ")}</p>}
    </div>
  );
}

function WebhookCard({ integration, webhookSecret }) {
  const url = `${BACKEND}/api/v1/integrations/webhooks/${integration.id}`;
  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copied"); };
  return (
    <div className="bg-card border border-border rounded-xl p-5 space-y-2" data-testid="webhook-card">
      <h2 className="text-sm font-bold text-foreground flex items-center gap-2"><Webhook className="h-4 w-4 text-primary" /> Webhook endpoint</h2>
      <p className="text-xs text-muted-foreground">POST HMAC-signed JSON here. Header <code className="bg-muted px-1 rounded">X-Signature-256</code>.</p>
      <div className="flex items-center gap-2 bg-background border border-border rounded-lg px-2 py-1.5">
        <span className="text-[11px] text-foreground truncate flex-1" data-testid="webhook-url">{url}</span>
        <button onClick={() => copy(url)} data-testid="webhook-copy"><Copy className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" /></button>
      </div>
    </div>
  );
}

export default function IntegrationDetail() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const urlTenant = sp.get("tenantId") || "";
  const scope = useTenantScope();
  const tenantId = scope.isSuper ? (scope.tenantId || urlTenant) : "";
  const params = tenantId ? { tenantId } : {};
  const suffix = tenantId ? `?tenantId=${tenantId}` : "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [newTarget, setNewTarget] = useState("");

  // sync the scope picker to the URL tenant on first load (SuperAdmin deep links)
  useEffect(() => { if (scope.isSuper && urlTenant && !scope.tenantId) scope.setTenantId(urlTenant); }, [urlTenant]);

  const blocked = scope.isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setData(null); return; }
    setLoading(true);
    integrationService.get(id, params)
      .then(setData)
      .catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [blocked, id, tenantId]);
  useEffect(() => { load(); }, [load]);

  const integration = data?.integration;
  const supportedTargets = integration?.connector?.supportedTargets || [];
  const available = integration?.connector?.available;

  const test = async () => {
    setBusy("test");
    try { const r = await integrationService.test(id, params); toast[r.ok ? "success" : "error"](r.detail); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const createJob = async () => {
    const target = newTarget || supportedTargets[0];
    if (!target) return;
    setBusy("job");
    try {
      await integrationService.createJob({ integrationId: id, name: `Sync ${target}`, targetEntity: target, tenantId: tenantId || undefined });
      toast.success("Sync job created");
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const runJob = async (job) => {
    setBusy(job.id);
    try { const r = await integrationService.runJob(job.id, params); toast.success(r.summary); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const toggleJob = async (job) => {
    try { await (job.enabled ? integrationService.disableJob : integrationService.enableJob)(job.id, params); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const removeIntegration = async () => {
    if (!window.confirm("Delete this integration and its sync jobs?")) return;
    try { await integrationService.remove(id, params); toast.success("Deleted"); window.history.back(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="integration-detail-page">
      <PageHeader icon={(p) => <ConnectorIcon code={integration?.connectorCode || ""} {...p} />}
        title={integration?.name || "Integration"}
        subtitle={integration ? `${integration.connector?.name} · ${integration.direction}` : ""}>
        <TenantPicker {...scope} />
      </PageHeader>

      <Link to="/admin/integrations/list" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4" data-testid="integration-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Integrations
      </Link>

      {blocked ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center"><p className="text-sm text-muted-foreground">Select a tenant to view this integration.</p></div>
      ) : loading && !data ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : integration ? (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="integration-summary">
            <div className="flex items-center gap-2">
              <Badge label={integration.status} styleMap={STATUS_STYLE} />
              <span className="text-xs text-muted-foreground">{integration.description}</span>
            </div>
            <div className="flex items-center gap-2">
              {available && (
                <button onClick={test} disabled={busy === "test"} className="inline-flex items-center gap-1.5 border border-border rounded-lg px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted disabled:opacity-50" data-testid="integration-test">
                  {busy === "test" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlugZap className="h-3.5 w-3.5" />} Test connection
                </button>
              )}
              <button onClick={removeIntegration} className="inline-flex items-center gap-1.5 border border-border rounded-lg px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-500/10" data-testid="integration-delete">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          </div>

          {!available && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-xs text-amber-700" data-testid="placeholder-notice">
              This is a connector-ready placeholder. Credentials can be stored, but syncing arrives in a later phase.
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-4">
            <CredentialForm integration={integration} authSchema={integration.connector?.authSchema} params={params} onSaved={load} />
            {integration.connectorCode === "FILE_UPLOAD" && <FileUploadCard integration={integration} params={params} onUploaded={load} />}
            {integration.connectorCode === "WEBHOOK" && <WebhookCard integration={integration} webhookSecret={data.webhookSecret} />}
          </div>

          {/* Mappings */}
          <div className="bg-card border border-border rounded-xl p-5" data-testid="mappings-section">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-foreground flex items-center gap-2"><GitBranch className="h-4 w-4 text-primary" /> Field mappings</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {supportedTargets.map((t) => {
                const m = (data.mappings || []).find((x) => x.targetEntity === t);
                return (
                  <Link key={t} to={`/admin/integrations/${id}/mapping/${t}${suffix}`}
                    className="inline-flex items-center gap-1.5 border border-border rounded-lg px-3 py-1.5 text-xs font-semibold text-foreground hover:border-primary/40 hover:bg-muted/40" data-testid={`mapping-link-${t}`}>
                    <Shuffle className="h-3.5 w-3.5 text-primary" /> {t}
                    {m ? <span className="text-emerald-600 text-[10px]">· {m.fields?.length || 0} fields</span> : <span className="text-muted-foreground text-[10px]">· not mapped</span>}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Sync jobs */}
          <div className="bg-card border border-border rounded-xl p-5" data-testid="syncjobs-section">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h2 className="text-sm font-bold text-foreground flex items-center gap-2"><Workflow className="h-4 w-4 text-primary" /> Sync jobs</h2>
              <div className="flex items-center gap-2">
                <select value={newTarget} onChange={(e) => setNewTarget(e.target.value)} className="bg-background border border-border rounded-lg px-2 py-1.5 text-xs text-foreground" data-testid="newjob-target">
                  <option value="">{supportedTargets[0] || "target"}</option>
                  {supportedTargets.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <button onClick={createJob} disabled={busy === "job"} className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid="create-job">
                  <Plus className="h-3.5 w-3.5" /> New job
                </button>
              </div>
            </div>
            {(data.syncJobs || []).length === 0 ? <p className="text-xs text-muted-foreground">No sync jobs yet.</p> : (
              <div className="space-y-2">
                {data.syncJobs.map((j) => (
                  <div key={j.id} className="flex items-center gap-3 border border-border rounded-lg p-3 flex-wrap" data-testid={`syncjob-${j.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{j.name} <span className="text-[11px] text-muted-foreground">→ {j.targetEntity}</span></p>
                      <p className="text-[11px] text-muted-foreground">Last run {fmtDate(j.lastRunAt)} {j.lastRunStatus && "· "}{j.lastRunStatus && <Badge label={j.lastRunStatus} styleMap={RUN_STYLE} />}</p>
                    </div>
                    <button onClick={() => toggleJob(j)} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${j.enabled ? "text-emerald-600 bg-emerald-500/10 border-emerald-500/30" : "text-muted-foreground bg-muted/40 border-border"}`} data-testid={`job-toggle-${j.id}`}>
                      <Power className="h-3 w-3 inline mr-0.5" />{j.enabled ? "Enabled" : "Disabled"}
                    </button>
                    <button onClick={() => runJob(j)} disabled={busy === j.id || !available} className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid={`job-run-${j.id}`}>
                      {busy === j.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Run
                    </button>
                    <Link to={`/admin/integrations/sync-logs?jobId=${j.id}${tenantId ? `&tenantId=${tenantId}` : ""}`} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" data-testid={`job-logs-${j.id}`}>
                      <RefreshCw className="h-3 w-3" /> Logs
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
